import type { FC, ReactElement } from 'react'
import { useIntl } from 'react-intl'

import { Button, Icon, Typography, useTheme } from '@themenu/design-system'

import { Loader } from '../../components/Loader'
import { BackButtonLayout } from '../../components/Shared/BackButtonLayout'
import { environmentVariables } from '../../utils/environmentVariables'

export type NativeAppHandoffPageProps = {
  blocked: boolean
  // The permission that unblocks the hand-off differs per OS (Android: a Chrome
  // toggle; iOS: a system "Open" prompt) so the message can't be a single shared string.
  platform: 'ios' | 'android'
  onRetry: () => void
  onStartOver: () => void
}

/**
 * Shown on `/signin/ios` and `/signin/android` while control is being handed back to
 * the native app, instead of leaving a blank tab when the hand-off is blocked (Custom
 * Tabs / auth-webview refusing an external-scheme navigation without user activation).
 * Presentational only (prop-driven, no side effects) so it can be storied in isolation —
 * see `useNativeAppHandoff` for the detection logic.
 *
 * `onStartOver` is the escape hatch off this screen: a leftover marker from an
 * unrelated previous attempt (crash, OS-killed tab, shared device) can render this
 * screen for a user who never actually attempted this sign-in — without it, that user
 * would be stuck behind `onRetry`, which replays a likely-unrelated attempt.
 */
export const NativeAppHandoffPage: FC<NativeAppHandoffPageProps> = ({
  blocked,
  platform,
  onRetry,
  onStartOver,
}): ReactElement => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  if (!blocked) {
    return (
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-24 px-20">
        <Loader />
        <Typography variant="body" component="p">
          {formatMessage({ id: 'sign_in.native_handoff.redirecting' })}
        </Typography>
      </div>
    )
  }

  return (
    <BackButtonLayout showBackButton={false}>
      <div data-testid="nativeHandoffWarning" className="flex flex-col items-center gap-60 p-24">
        <div
          className="flex justify-center rounded-full align-items-center"
          style={{ height: '88px', width: '88px', backgroundColor: theme.colors.background.critical }}
        >
          <Icon name="cancel" color={theme.colors.content.critical} size={32} />
        </div>

        <div className="flex flex-col items-center justify-center gap-24">
          <Typography variant="h1" component="h1">
            {formatMessage({ id: 'sign_in.native_handoff.blocked_title' })}
          </Typography>
          <Typography variant="body" component="p">
            {formatMessage({
              id:
                platform === 'ios'
                  ? 'sign_in.native_handoff.blocked_message.ios'
                  : 'sign_in.native_handoff.blocked_message.android',
            })}
          </Typography>
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-8">
          <Button onClick={onRetry} variant="primary" className="w-full">
            {formatMessage({ id: 'sign_in.native_handoff.retry' })}
          </Button>
          <Button onClick={onStartOver} variant="secondary" className="w-full">
            {formatMessage({ id: 'sign_in.native_handoff.start_over' })}
          </Button>
          <Button variant="alternativeText" component="a" href={environmentVariables('HELP_URL')} className="w-full">
            {formatMessage({ id: 'help.faq' })}
          </Button>
        </div>
      </div>
    </BackButtonLayout>
  )
}
